/**
 * SubscriptionRequiredRoute Component
 *
 * Wrapper component that requires authentication, a completed profile,
 * AND an active subscription (or valid trial) to access child routes.
 *
 * Delegates auth + profile + onboarding checks to ProfileRequiredRoute, then checks
 * subscription status via useSubscriptionStatus(). When can_use_features
 * is false, displays a paywall screen with the reason and a link to billing.
 *
 * Note: ProfileRequiredRoute already handles onboarding redirect, so by the time
 * SubscriptionGate renders, onboarding is guaranteed to be complete.
 */

import { Link } from "react-router-dom";
import { useSubscriptionStatus } from "../hooks/useBilling";
import { ProfileRequiredRoute } from "./ProfileRequiredRoute";
import { LoadingSpinner } from "./LoadingSpinner";

interface SubscriptionRequiredRouteProps {
  children: React.ReactNode;
}

function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { data: status, isLoading } = useSubscriptionStatus();

  if (isLoading) {
    return <LoadingSpinner message="Checking subscription..." />;
  }

  if (!status || status.can_use_features === false) {
    const reason =
      status?.reason ||
      "An active subscription is required to use this feature.";

    return (
      <div className="flex min-h-screen items-center justify-center bg-muted p-4">
        <div className="w-full max-w-md rounded-lg bg-card p-8 text-center shadow-md">
          <div className="mb-4 text-4xl">🔒</div>
          <h2 className="mb-2 text-xl font-semibold text-foreground">
            Subscription Required
          </h2>
          <p className="mb-6 text-sm text-muted-foreground">{reason}</p>
          <Link
            to="/account?tab=billing"
            className="inline-block rounded-md bg-indigo-600 dark:bg-indigo-700 px-6 py-2 text-sm font-medium text-white hover:bg-indigo-700 dark:hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            View Plans & Subscribe
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export function SubscriptionRequiredRoute({
  children,
}: SubscriptionRequiredRouteProps) {
  return (
    <ProfileRequiredRoute>
      <SubscriptionGate>{children}</SubscriptionGate>
    </ProfileRequiredRoute>
  );
}
